/**
 * RecipientActions Component
 *
 * Action buttons, channel badges, and menu for recipient card.
 * Handles verification action, test button, channel badges, and actions menu.
 *
 * @module RecipientItem/RecipientActions
 */

import {
  ActionList,
  Badge,
  Box,
  Button,
  Icon,
  InlineStack,
  Popover,
  Spinner,
  Text,
  Tooltip,
} from '@shopify/polaris';
import {
  AlertTriangleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClockIcon,
  DeleteIcon,
  EditIcon,
  EmailIcon,
  MenuHorizontalIcon,
  SendIcon,
} from '@shopify/polaris-icons';

import type { AlertLevel, ChannelStatus, VerificationState } from './types';
import { ARIA_LABELS } from './constants';
import { getAlertLevelIcon, getAlertLevelTone, getAlertLevelLabel } from './utils';

export interface RecipientActionsProps {
  /** Recipient name for accessibility labels */
  recipientName: string;
  /** Whether the recipient is verified */
  isVerified: boolean;
  /** Current verification state */
  verificationState: VerificationState;
  /** Whether actions menu is open */
  showActionsMenu: boolean;
  /** Callback to toggle actions menu */
  onToggleActionsMenu: () => void;
  /** Callback when verify button is clicked */
  onVerify: () => void;
  /** Callback when test button is clicked */
  onTest: () => void;
  /** Callback when edit is clicked */
  onEdit: () => void;
  /** Callback when delete is clicked */
  onDelete: () => void;
  /** Hide the verification button (when showing inline code input) */
  hideVerificationButton?: boolean;
  /** Channel statuses */
  channels?: {
    digest: ChannelStatus;
    instant: ChannelStatus;
  };
  /** Alert levels for the recipient */
  alertLevels?: AlertLevel[];
  /** Whether digest toggle is loading */
  isTogglingDigest?: boolean;
  /** Whether instant toggle is loading */
  isTogglingInstant?: boolean;
  /** Callback when digest channel is toggled */
  onToggleDigest?: () => void;
  /** Callback when instant (alerts) channel is toggled */
  onToggleInstant?: () => void;
  /** Whether alert badges are expanded */
  alertBadgesExpanded?: boolean;
  /** Callback to toggle alert badges */
  onToggleAlertBadges?: () => void;
  /** Whether to show pop animation on badges */
  showBadgesPopAnimation?: boolean;
}

/**
 * Recipient action buttons component (Desktop)
 *
 * Displays:
 * - Channel badges (Digest, Alerts)
 * - Verification button (idle state)
 * - Test button (verified state)
 * - Loading spinner (sending/testing states)
 * - Actions menu (edit, delete)
 */
export function RecipientActions({
  recipientName,
  isVerified,
  verificationState,
  showActionsMenu,
  onToggleActionsMenu,
  onVerify,
  onTest,
  onEdit,
  onDelete,
  hideVerificationButton = false,
  channels,
  alertLevels = [],
  isTogglingDigest = false,
  isTogglingInstant = false,
  onToggleDigest,
  onToggleInstant,
  alertBadgesExpanded = false,
  onToggleAlertBadges,
  showBadgesPopAnimation = false,
}: RecipientActionsProps) {
  // Render verification action based on state
  const renderVerificationAction = () => {
    if (hideVerificationButton) return null;

    switch (verificationState) {
      case 'idle':
        return (
          <Tooltip content="Send verification email">
            <Button
              size="slim"
              variant="secondary"
              onClick={onVerify}
              icon={EmailIcon}
              accessibilityLabel={`Verify ${recipientName}`}
            >
              Verify
            </Button>
          </Tooltip>
        );

      case 'sending':
        return (
          <Box paddingInline="200">
            <Spinner size="small" />
          </Box>
        );

      case 'awaiting_code':
      case 'error':
      case 'verifying':
      case 'verified':
        return null; // Handled by inline input or dropdown menu

      case 'testing':
        return (
          <Box paddingInline="200">
            <Spinner size="small" />
          </Box>
        );

      default:
        return null;
    }
  };

  // Build action list items
  const actionItems = [
    // Test action (only for verified recipients)
    ...(isVerified
      ? [
          {
            content: 'Send test email',
            icon: SendIcon,
            onAction: () => {
              onToggleActionsMenu();
              onTest();
            },
          },
        ]
      : []),
    // Edit action
    {
      content: 'Edit',
      icon: EditIcon,
      onAction: () => {
        onToggleActionsMenu();
        onEdit();
      },
    },
    // Delete action
    {
      content: 'Delete',
      icon: DeleteIcon,
      destructive: true,
      onAction: () => {
        onToggleActionsMenu();
        onDelete();
      },
    },
  ];

  // Render digest badge
  const renderDigestBadge = () => {
    if (!isVerified || !channels) return null;

    const isEnabled = channels.digest.enabled;
    const tooltipContent = isEnabled
      ? 'Digest enabled - click to disable'
      : 'Enable digest notifications';

    return (
      <Tooltip content={tooltipContent}>
        <span>
          <button
            type="button"
            onClick={onToggleDigest}
            disabled={isTogglingDigest}
            className={`channel-badge ${isEnabled ? 'channel-badge-enabled' : 'channel-badge-disabled'} ${showBadgesPopAnimation ? 'recipient-verified-pop' : ''}`}
            aria-label={ARIA_LABELS.channelToggle('Digest', isEnabled)}
            aria-pressed={isEnabled}
          >
            {isTogglingDigest ? (
              <Spinner size="small" />
            ) : (
              <InlineStack gap="050" blockAlign="center" wrap={false}>
                <Icon source={ClockIcon} tone={isEnabled ? 'info' : 'subdued'} />
                <Text as="span" variant="bodySm" tone={isEnabled ? undefined : 'subdued'}>
                  Digest
                </Text>
              </InlineStack>
            )}
          </button>
        </span>
      </Tooltip>
    );
  };

  // Render combined alerts badge (instant + alert levels)
  const renderAlertsBadge = () => {
    if (!isVerified || !channels) return null;

    const isEnabled = channels.instant.enabled;
    const hasAlertLevels = alertLevels.length > 0;

    // Tooltip shows alert levels if enabled
    const tooltipContent = isEnabled
      ? hasAlertLevels
        ? `Alerts enabled for ${alertLevels.length} level${alertLevels.length !== 1 ? 's' : ''} - click to ${alertBadgesExpanded ? 'collapse' : 'expand'}`
        : 'Alerts enabled - click to disable'
      : 'Enable instant alerts';

    // When expanded, show alert level badges
    if (alertBadgesExpanded && isEnabled && hasAlertLevels) {
      return (
        <button
          type="button"
          onClick={onToggleAlertBadges}
          className={`alert-badges-toggle ${showBadgesPopAnimation ? 'recipient-alert-levels-pop' : ''}`}
          aria-expanded={true}
          aria-label={ARIA_LABELS.COLLAPSE_ALERTS}
        >
          <InlineStack gap="100" blockAlign="center" wrap={false}>
            {alertLevels.map((level, index) => (
              <span
                key={level}
                className="alert-badge-item"
                data-animation-delay={index}
              >
                <Badge
                  tone={getAlertLevelTone(level)}
                  icon={getAlertLevelIcon(level)}
                  size="small"
                >
                  {getAlertLevelLabel(level)}
                </Badge>
              </span>
            ))}
            <span className="alert-badge-chevron">
              <Icon source={ChevronUpIcon} tone="subdued" />
            </span>
          </InlineStack>
        </button>
      );
    }

    // Determine click handler based on state
    const handleClick = () => {
      if (!isEnabled || !hasAlertLevels) {
        // When disabled or no alert levels configured, open edit modal
        onEdit();
      } else if (onToggleAlertBadges) {
        // When enabled with levels, expand/collapse
        onToggleAlertBadges();
      }
    };

    // Collapsed state - show "Alerts" badge
    return (
      <Tooltip content={isEnabled ? tooltipContent : 'Click to configure alerts'}>
        <span>
          <button
            type="button"
            onClick={handleClick}
            disabled={isTogglingInstant}
            className={`channel-badge ${isEnabled ? 'channel-badge-enabled' : 'channel-badge-disabled'} ${showBadgesPopAnimation ? 'recipient-verified-pop' : ''}`}
            aria-label={isEnabled && hasAlertLevels ? ARIA_LABELS.EXPAND_ALERTS : 'Configure alerts'}
            aria-pressed={isEnabled}
          >
            {isTogglingInstant ? (
              <Spinner size="small" />
            ) : (
              <InlineStack gap="050" blockAlign="center" wrap={false}>
                <Icon source={AlertTriangleIcon} tone={isEnabled ? 'warning' : 'subdued'} />
                <Text as="span" variant="bodySm" tone={isEnabled ? undefined : 'subdued'}>
                  Alerts
                </Text>
                {isEnabled && hasAlertLevels && (
                  <span className="alert-badge-chevron">
                    <Icon source={ChevronDownIcon} tone="subdued" />
                  </span>
                )}
              </InlineStack>
            )}
          </button>
        </span>
      </Tooltip>
    );
  };

  return (
    <InlineStack gap="200" blockAlign="center">
      {/* Channel badges - aligned with actions */}
      {isVerified && channels && (
        <InlineStack gap="100" blockAlign="center" wrap={false}>
          {renderDigestBadge()}
          {renderAlertsBadge()}
        </InlineStack>
      )}

      {/* Verification/Test action */}
      <div>{renderVerificationAction()}</div>

      {/* Actions Menu (Test, Edit, Delete) */}
      <Box opacity={isVerified ? undefined : '50'}>
        <Popover
          active={showActionsMenu}
          activator={
            <Tooltip content="More actions">
              <Button
                size="slim"
                variant="tertiary"
                icon={MenuHorizontalIcon}
                onClick={onToggleActionsMenu}
                accessibilityLabel={ARIA_LABELS.ACTIONS_MENU}
              />
            </Tooltip>
          }
          onClose={onToggleActionsMenu}
          preferredAlignment="right"
        >
          <ActionList items={actionItems} />
        </Popover>
      </Box>
    </InlineStack>
  );
}

/**
 * Mobile-specific actions menu
 *
 * Simplified menu for mobile devices with all actions in a popover.
 */
export interface MobileRecipientActionsProps {
  /** Whether the recipient is verified */
  isVerified: boolean;
  /** Current verification state */
  verificationState: VerificationState;
  /** Whether actions menu is open */
  showActionsMenu: boolean;
  /** Callback to toggle actions menu */
  onToggleActionsMenu: () => void;
  /** Callback when verify is clicked */
  onVerify: () => void;
  /** Callback when test is clicked */
  onTest: () => void;
  /** Callback when edit is clicked */
  onEdit: () => void;
  /** Callback when delete is clicked */
  onDelete: () => void;
}

export function MobileRecipientActions({
  isVerified,
  verificationState,
  showActionsMenu,
  onToggleActionsMenu,
  onVerify,
  onTest,
  onEdit,
  onDelete,
}: MobileRecipientActionsProps) {
  // Build action list items for mobile
  const mobileActionItems = [
    // Verification actions based on state
    ...(verificationState === 'idle'
      ? [
          {
            content: 'Send verification email',
            icon: EmailIcon,
            onAction: () => {
              onToggleActionsMenu();
              onVerify();
            },
          },
        ]
      : []),
    ...(verificationState === 'verified'
      ? [
          {
            content: 'Send test email',
            icon: SendIcon,
            onAction: () => {
              onToggleActionsMenu();
              onTest();
            },
          },
        ]
      : []),
    // Edit action
    {
      content: 'Edit',
      icon: EditIcon,
      onAction: () => {
        onToggleActionsMenu();
        onEdit();
      },
    },
    // Delete action
    {
      content: 'Delete',
      icon: DeleteIcon,
      destructive: true,
      onAction: () => {
        onToggleActionsMenu();
        onDelete();
      },
    },
  ];

  return (
    <Popover
      active={showActionsMenu}
      activator={
        <Button
          size="slim"
          variant="tertiary"
          icon={MenuHorizontalIcon}
          onClick={onToggleActionsMenu}
          accessibilityLabel={ARIA_LABELS.ACTIONS_MENU}
        />
      }
      onClose={onToggleActionsMenu}
      preferredAlignment="right"
    >
      <ActionList items={mobileActionItems} />
    </Popover>
  );
}
